/**
 * Login Form Component for Stenope Academy
 * 
 * Provides user authentication with same look as Stenope.AI
 */

import React, { useState } from 'react';
import {
    Box,
    Paper,
    TextField,
    Button,
    Typography,
    Alert,
    InputAdornment,
    IconButton,
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    Person,
    Lock,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { socketService } from '../services/socketService';
import { ACADEMY_AUTH_TOKEN_KEY } from '../utils/authStorage';
import Logo from './Logo';
import LoadingScreen from "./LoadingScreen";

const LoginForm = () => {
    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const reason = queryParams.get('reason');

    const [formData, setFormData] = useState({
        username: '',
        password: '',
    });
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
        if (error) setError('');
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        setError('');

        socketService.disconnect();

        if (!formData.username || !formData.password) {
            setError('Please fill in all fields');
            return;
        }

        setIsSubmitting(true);

        try {
            const normalizedUsername = formData.username.includes('@')
                ? formData.username.toLowerCase()
                : formData.username;

            const result = await login(normalizedUsername, formData.password);

            if (result.success) {
                const token = localStorage.getItem(ACADEMY_AUTH_TOKEN_KEY);
                if (token) {
                    socketService.connect(token);
                }
                navigate('/dashboard');
            } else {
                setError(result.message || 'Login failed');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Login failed');
        } finally {
            setIsSubmitting(false);
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };
    if (isSubmitting) {
  return <LoadingScreen />;
}

    return (
        <Box
            sx={(theme) => ({
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: theme.palette.mode === 'light'
                    ? 'radial-gradient(circle at top left, #F2F2F7 0%, #E3E7FF 40%, #F5F5F7 100%)'
                    : 'radial-gradient(circle at top left, #050212 0%, #03001C 60%, #09051d 100%)',
                padding: 3,
                position: 'relative',
                overflow: 'hidden',
            })}
        >
            {/* Drifting Ambient Background Orbs */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '15%',
                    left: '15%',
                    width: { xs: '300px', md: '500px' },
                    height: { xs: '300px', md: '500px' },
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(99, 102, 241, 0.16) 0%, transparent 70%)',
                    filter: 'blur(80px)',
                    zIndex: 0,
                    animation: 'floatOrb1 20s ease-in-out infinite alternate',
                    pointerEvents: 'none',
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: '10%',
                    right: '15%',
                    width: { xs: '350px', md: '550px' },
                    height: { xs: '350px', md: '550px' },
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(236, 72, 153, 0.12) 0%, transparent 70%)',
                    filter: 'blur(100px)',
                    zIndex: 0,
                    animation: 'floatOrb2 25s ease-in-out infinite alternate',
                    pointerEvents: 'none',
                }}
            />

            <Paper
                elevation={0}
                sx={(theme) => ({
                    padding: { xs: 3.5, sm: 5 },
                    width: '100%',
                    maxWidth: 420,
                    borderRadius: '24px',
                    background: theme.palette.mode === 'light'
                        ? 'rgba(255, 255, 255, 0.75)'
                        : 'rgba(15, 23, 42, 0.45)',
                    backdropFilter: 'blur(20px)',
                    border: theme.palette.mode === 'light'
                        ? '1px solid rgba(99, 102, 241, 0.15)'
                        : '1px solid rgba(255, 255, 255, 0.08)',
                    boxShadow: theme.palette.mode === 'light'
                        ? '0 20px 40px rgba(99, 102, 241, 0.06)'
                        : '0 25px 50px rgba(0, 0, 0, 0.45), inset 0 1px 0px rgba(255, 255, 255, 0.1)',
                    position: 'relative',
                    zIndex: 1,
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.palette.mode === 'light'
                            ? '0 24px 48px rgba(99, 102, 241, 0.1)'
                            : '0 30px 60px rgba(0, 0, 0, 0.55), inset 0 1px 0px rgba(255, 255, 255, 0.15)',
                    }
                })}
            >
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                        <Logo height="54px" />
                    </Box>
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{
                            fontWeight: 800,
                            background: 'linear-gradient(135deg, #8B5CF6 10%, #D946EF 90%)',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            mb: 1.5,
                            letterSpacing: '-0.5px',
                        }}
                    >
                        Welcome Back
                    </Typography>
                    <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontWeight: 500, letterSpacing: '0.2px' }}
                    >
                        Sign in to Stenope Academy
                    </Typography>
                </Box>

                {reason === 'session_expired' && !error && (
                    <Alert 
                        severity="warning" 
                        sx={{ 
                            mb: 3, 
                            borderRadius: '12px',
                            fontWeight: 500,
                            border: '1px solid rgba(245, 158, 11, 0.2)'
                        }}
                    >
                        Session Expired: Logged out because this account was logged in on another device.
                    </Alert>
                )}

                {error && (
                    <Alert 
                        severity="error" 
                        sx={{ 
                            mb: 3, 
                            borderRadius: '12px',
                            fontWeight: 500,
                            border: '1px solid rgba(239, 68, 68, 0.2)'
                        }}
                    >
                        {error}
                    </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="username"
                        label="Username or Email"
                        name="username"
                        autoComplete="username"
                        autoFocus
                        value={formData.username}
                        onChange={handleChange}
                        disabled={isSubmitting}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Person sx={{ color: 'text.secondary', opacity: 0.7 }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ 
                            mb: 2.5,
                            '& .MuiInputLabel-root': {
                                fontWeight: 500,
                            }
                        }}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        autoComplete="current-password"
                        value={formData.password}
                        onChange={handleChange}
                        disabled={isSubmitting}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock sx={{ color: 'text.secondary', opacity: 0.7 }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={togglePasswordVisibility}
                                edge="end"
                                disabled={isSubmitting}
                                sx={{ color: 'text.secondary', opacity: 0.7 }}
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={{ 
                            mb: 4,
                            '& .MuiInputLabel-root': {
                                fontWeight: 500,
                            }
                        }}
                    />

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={isSubmitting}
                        sx={{
                            mb: 1.5,
                            py: 1.8,
                            fontSize: '1rem',
                            fontWeight: 700,
                            letterSpacing: '0.5px',
                        }}
                    >
                        Sign In
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default LoginForm;
